import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import { STATIC_PAGES } from "../seo/openGraph";
import { motion } from "framer-motion";
import {
  Smartphone, Globe, Code, ShoppingCart, Brain, ArrowRight,
  Users, Zap, Award, CheckCircle, ChevronRight, Send, Timer, BarChart3, Lock,
  ListChecks,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  LEAD_CARD_CLASS,
  LEAD_FIELD_CLASS,
  LEAD_SUBCOPY_CLASS,
  LEAD_TEXTAREA_CLASS,
} from "../components/leadMagnetStyles";
import IdeaValidatorModal from "../components/IdeaValidator/IdeaValidatorModal";
import AppCostEstimatorModal from "../components/AppCostEstimator/AppCostEstimatorModal";
import SpecializedSolutionsHub from "../components/SpecializedSolutionsHub";
import { getLandingPageBySlug } from "../data/landingPages";
import { trackLead } from "@/lib/analytics";
import {
  HoneypotField,
  LEAD_SUBMIT_ERROR,
  LeadFormError,
  honeypotValue,
  submitAppsContact,
  useLeadFormGuard,
} from "@/lib/hubspot";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" },
  }),
};

const stats = [
  { value: "2002", label: "Founded" },
  { value: "$100M+", label: "Contract Value" },
  { value: "221+", label: "Clients" },
  { value: "Clutch", label: "Rated" },
];

const services = [
  {
    icon: Smartphone,
    title: "Mobile App Development",
    desc: "Native iOS, Android, and cross-platform apps built for performance and scale.",
    path: "/services/mobile-app-development",
  },
  {
    icon: Globe,
    title: "Web App Development",
    desc: "Modern web applications with React, Angular, Vue, and enterprise-grade backends.",
    path: "/services/web-app-development",
  },
  {
    icon: Code,
    title: "Custom Software",
    desc: "Bespoke solutions engineered to solve your most complex business challenges.",
    path: "/services/custom-software",
  },
  {
    icon: ShoppingCart,
    title: "E-commerce Apps",
    desc: "End-to-end commerce platforms that drive revenue and customer loyalty.",
    path: "/services/ecommerce",
  },
  {
    icon: Brain,
    title: "AI & Machine Learning",
    desc: "LLM, vision, and forecasting features that survive production, with evals, not just a demo.",
    path: "/services/ai-ml",
  },
];

const process = [
  { step: "01", title: "Discover", desc: "Name the constraint before we write code." },
  { step: "02", title: "Design", desc: "Enough UI to decide, not a 40-page deck." },
  { step: "03", title: "Develop", desc: "Senior engineers. Two-week slices you can see." },
  { step: "04", title: "Deploy", desc: "Ship, measure, change. We stay for the ugly part." },
];

const caseStudies = [
  { name: "AT&T", category: "Software Engineering", desc: "Enterprise mobile and web work for AT&T Government Solutions.", initials: "AT&T", color: "#00AEEF", url: "https://www.maxiomtech.com/case-study/att/" },
  { name: "Microsoft", category: "MVP Development", desc: "MVP prototyping and deployment for Microsoft teams.", initials: "MS", color: "#10B981", url: "https://www.maxiomtech.com/case-study/microsoft/" },
  { name: "BMW Performance Center", category: "Creative Design", desc: "Digital experience for BMW's performance driving center.", initials: "BMW", color: "#F59E0B", url: "https://www.maxiomtech.com/case-study/bmw-performance-center/" },
  { name: "Toastmasters International", category: "AI & Software", desc: "Platform work for the global public-speaking organization.", initials: "TM", color: "#8B5CF6", url: "https://www.maxiomtech.com/case-study/toastmasters-international/" },
];

const testimonials = [
  {
    quote: "Maxiom's ability to work with our internal Scrum team, coaching best practices and avoiding pitfalls was essential. A clear leader.",
    name: "Quentin Gilbert",
    title: "Engineering Lead, AT&T Government Solutions",
    photo: "https://www.maxiomtech.com/images/testimonials/team4.jpg",
  },
  {
    quote: "The rare quality that stands out with Maxiom Technology is ingenuity. They have the answer to any problem where others fail.",
    name: "Michael Atkins",
    title: "VP of Technology, Toastmasters International",
    photo: "https://www.maxiomtech.com/images/testimonials/michael-atkins-toastmasters-international.jpeg",
  },
  {
    quote: "High caliber software developers with an excellent track record of successful software projects delivery.",
    name: "Alfonso Davila",
    title: "Director of Engineering, Buchanan-Edwards",
    photo: "https://www.maxiomtech.com/images/testimonials/alfonso-davila-buchanan-edwards.jpeg",
  },
];

function LeadCaptureForm({ onFirstNameChange } = {}) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { companyUrl, setCompanyUrl, mountedAt } = useLeadFormGuard();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const outcome = await submitAppsContact(
        {
          email: form.email,
          name: form.name,
          company: form.company,
          message: form.message,
          source: "home_contact_form",
        },
        {
          companyUrl: honeypotValue(e, companyUrl),
          mountedAt,
          name: form.name,
        },
      );
      if (outcome === "sent") trackLead("home_contact_form");
      setSubmitted(true);
    } catch {
      setError(LEAD_SUBMIT_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNameChange = (value) => {
    setForm({ ...form, name: value });
    // Prefill the modal capture using the user's full lead name.
    onFirstNameChange?.(value.trim());
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${LEAD_CARD_CLASS} rounded-xl p-10 text-center`}
        data-testid="lead-capture-thank-you"
      >
        <CheckCircle className="w-12 h-12 text-[#00AEEF] mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
        <p className="text-gray-200 text-sm">
          A senior engineer will read this and reply within one business day.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={1}
      onSubmit={handleSubmit}
      className={`${LEAD_CARD_CLASS} rounded-xl p-8 space-y-5`}
      data-testid="lead-capture-form"
      data-hs-do-not-collect="true"
      aria-busy={submitting}
    >
      <HoneypotField value={companyUrl} onChange={setCompanyUrl} />
      <h3 className="text-xl font-semibold text-white mb-1">
        Get a Free Consultation
      </h3>
      <p className={`${LEAD_SUBCOPY_CLASS} mb-4`}>A senior engineer replies within one business day.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          name="name"
          required
          placeholder="Full Name *"
          value={form.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className={LEAD_FIELD_CLASS}
          data-testid="lead-input-name"
        />
        <Input
          name="email"
          type="email"
          required
          placeholder="Email *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={LEAD_FIELD_CLASS}
          data-testid="lead-input-email"
        />
      </div>
      <Input
        name="company"
        placeholder="Company Name"
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
        className={LEAD_FIELD_CLASS}
        data-testid="lead-input-company"
      />
      <textarea
        name="message"
        required
        placeholder="Tell us about your project *"
        rows={3}
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        className={LEAD_TEXTAREA_CLASS}
        data-testid="lead-input-message"
      />
      <LeadFormError message={error} testId="lead-form-error" />
      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium py-5 text-sm rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]"
        data-testid="lead-submit-button"
      >
        {submitting ? "Sending..." : "Get Your Free Consultation"}{" "}
        {!submitting && <Send className="ml-2 w-4 h-4" />}
      </Button>
    </motion.form>
  );
}

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Maxiom Apps",
  "alternateName": "Maxiom Apps",
  "url": "https://maxiomapps.com",
  "logo": "https://www.maxiomtech.com/images/maxiom-logo.png",
  "description": "Custom mobile, web, and AI app development from an Ashburn, VA studio. Shipping since 2002.",
  "foundingDate": "2002",
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "minValue": 50,
    "maxValue": 200
  },
  "sameAs": [
    "https://www.linkedin.com/company/maxiom-technology",
    "https://github.com/maxiomtech",
    "https://www.maxiomtech.com",
    "https://maxiom.dev",
    "https://www.maxiomlabs.com",
    "https://www.cto20.com"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-703-942-9420",
    "contactType": "sales",
    "email": "hello@maxiomtech.com",
    "availableLanguage": "English"
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "44927 George Washington Blvd Suite 265",
    "addressLocality": "Ashburn",
    "addressRegion": "VA",
    "postalCode": "20147",
    "addressCountry": "US"
  },
  "areaServed": "US",
  "knowsAbout": [
    "Mobile App Development",
    "iOS Development",
    "Android Development",
    "Web Application Development",
    "Custom Software Development",
    "AI and Machine Learning",
    "E-commerce Development",
    "SaaS Development"
  ]
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Maxiom Apps",
  "image": "https://www.maxiomtech.com/images/maxiom-logo.png",
  "url": "https://maxiomapps.com",
  "telephone": "+1-703-942-9420",
  "email": "hello@maxiomtech.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "44927 George Washington Blvd Suite 265",
    "addressLocality": "Ashburn",
    "addressRegion": "VA",
    "postalCode": "20147",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 39.0437,
    "longitude": -77.4875
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  },
  "priceRange": "$$$"
};

export default function HomePage() {
  const location = useLocation();
  const [ideaValidatorOpen, setIdeaValidatorOpen] = useState(false);
  const [ideaValidatorFirstName, setIdeaValidatorFirstName] = useState("");
  const [costEstimatorOpen, setCostEstimatorOpen] = useState(false);
  const ideaValidatorOpenRef = useRef(false);

  const costEstimateLanding = useMemo(
    () => getLandingPageBySlug("free-app-cost-estimate"),
    [],
  );

  useEffect(() => {
    ideaValidatorOpenRef.current = ideaValidatorOpen;
  }, [ideaValidatorOpen]);

  // Client-side navigations update the hash but browsers do not auto-scroll SPA routes.
  // Also depends on location.search when switching browseHub tab while hash stays unchanged.
  useEffect(() => {
    if (location.pathname !== "/" || location.hash !== "#browse-solutions") return;
    let cancelled = false;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        document
          .getElementById("browse-solutions")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, location.hash]);

  // Auto-show the quiz modal after:
  // - user stays on the page for a short period
  // - user scrolls past ~half of the page
  useEffect(() => {
    const storageKey = "ideaValidatorAutoOpenedV1";
    if (sessionStorage.getItem(storageKey)) return;

    // Time-based only (no scroll trigger) to avoid annoying interruptions.
    const readyTimer = window.setTimeout(() => {
      if (ideaValidatorOpenRef.current) return;
      sessionStorage.setItem(storageKey, "1");
      setIdeaValidatorOpen(true);
    }, 45000); // ~45 seconds

    return () => {
      window.clearTimeout(readyTimer);
    };
  }, []);

  return (
    <>
      <Seo {...STATIC_PAGES.home} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0A]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/8 via-transparent to-transparent" />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#00AEEF]/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10 pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-6"
              >
                App studio · iOS, Android & web
              </motion.p>
              <motion.h1
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={1}
                className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-8"
              >
                We build apps.
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5CCFFF] to-[#00AEEF]">
                  We'll tell you if you shouldn't.
                </span>
              </motion.h1>
              <motion.p
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={2}
                className="text-lg md:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed"
              >
                Native and cross-platform mobile, web, and custom software. Ashburn, VA studio, shipping since 2002. Senior engineers, not a bench. If the stack or the scope is wrong, we say so before you pay for a sprint.
              </motion.p>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={3}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/contact" data-testid="hero-cta-primary">
                  <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                    Schedule a Call <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/case-studies" data-testid="hero-cta-secondary">
                  <Button variant="outline" className="border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] px-8 py-6 text-base rounded-md transition-all duration-300">
                    View Our Work
                  </Button>
                </Link>
              </motion.div>
            </div>

            {/* Right - App Mockup Visual */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="hidden lg:block relative"
              data-testid="hero-visual"
            >
              <div className="relative w-full max-w-lg mx-auto">
                {/* Glow */}
                <div className="absolute -inset-4 bg-[#00AEEF]/10 rounded-3xl blur-2xl" />

                {/* Main phone */}
                <div className="relative bg-[#1a1a1a] rounded-[2.5rem] p-3 border border-white/10 shadow-2xl">
                  <div className="bg-[#0f0f0f] rounded-[2rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-6 py-2 bg-[#0f0f0f]">
                      <span className="text-[10px] text-gray-400">9:41</span>
                      <div className="w-20 h-5 bg-[#1a1a1a] rounded-full" />
                      <div className="flex gap-1">
                        <div className="w-3 h-2 bg-gray-500 rounded-sm" />
                        <div className="w-3 h-2 bg-gray-500 rounded-sm" />
                      </div>
                    </div>
                    {/* App content */}
                    <div className="px-5 py-4 space-y-4">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-9 h-9 rounded-xl bg-[#00AEEF] flex items-center justify-center">
                          <Zap className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-white text-sm font-semibold">Dashboard</div>
                          <div className="text-gray-400 text-[10px]">Real-time analytics</div>
                        </div>
                      </div>
                      {/* Metric cards */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
                          <div className="text-[10px] text-gray-400 mb-1">Revenue</div>
                          <div className="text-white text-lg font-bold">$142K</div>
                          <div className="text-[#10B981] text-[10px]">+23.5%</div>
                        </div>
                        <div className="bg-[#1a1a1a] rounded-xl p-3 border border-white/5">
                          <div className="text-[10px] text-gray-400 mb-1">Users</div>
                          <div className="text-white text-lg font-bold">8,492</div>
                          <div className="text-[#00AEEF] text-[10px]">+12.1%</div>
                        </div>
                      </div>
                      {/* Chart placeholder */}
                      <div className="bg-[#1a1a1a] rounded-xl p-4 border border-white/5">
                        <div className="text-[10px] text-gray-400 mb-3">Weekly Activity</div>
                        <div className="flex items-end gap-2 h-20">
                          {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                            <motion.div
                              key={i}
                              initial={{ height: 0 }}
                              animate={{ height: `${h}%` }}
                              transition={{ delay: 0.5 + i * 0.1, duration: 0.6, ease: "easeOut" }}
                              className="flex-1 rounded-md bg-gradient-to-t from-[#00AEEF] to-[#00AEEF]/40"
                            />
                          ))}
                        </div>
                        <div className="flex justify-between mt-2">
                          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                            <span key={i} className="text-[8px] text-neutral-400 flex-1 text-center">{d}</span>
                          ))}
                        </div>
                      </div>
                      {/* List items */}
                      <div className="space-y-2">
                        {[
                          { label: "New signups", val: "+284", color: "#00AEEF" },
                          { label: "Conversions", val: "12.4%", color: "#10B981" },
                          { label: "Avg. session", val: "4m 32s", color: "#8B5CF6" },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center justify-between bg-[#1a1a1a] rounded-lg px-3 py-2.5 border border-white/5">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="text-gray-400 text-xs">{item.label}</span>
                            </div>
                            <span className="text-white text-xs font-medium">{item.val}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Nav bar */}
                    <div className="flex items-center justify-around px-6 py-3 border-t border-white/5 bg-[#0f0f0f]">
                      <div className="w-5 h-5 rounded-md bg-[#00AEEF]" />
                      <div className="w-5 h-5 rounded-md bg-white/10" />
                      <div className="w-5 h-5 rounded-md bg-white/10" />
                      <div className="w-5 h-5 rounded-md bg-white/10" />
                    </div>
                  </div>
                </div>

                {/* Floating notification card */}
                <motion.div
                  initial={{ opacity: 0, y: 20, x: 20 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  transition={{ delay: 1.2, duration: 0.5 }}
                  className="absolute -right-8 top-20 bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 shadow-2xl backdrop-blur-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-[#10B981]" />
                    </div>
                    <div>
                      <div className="text-white text-[10px] font-medium">Build Deployed</div>
                      <div className="text-gray-400 text-[8px]">v2.4.1 (just now)</div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating code snippet */}
                <motion.div
                  initial={{ opacity: 0, y: -20, x: -20 }}
                  animate={{ opacity: 1, y: 0, x: 0 }}
                  transition={{ delay: 1.5, duration: 0.5 }}
                  className="absolute -left-6 bottom-28 bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 shadow-2xl font-mono text-[10px]"
                >
                  <span className="text-[#8B5CF6]">const</span>{" "}
                  <span className="text-[#00AEEF]">app</span>{" "}
                  <span className="text-white">=</span>{" "}
                  <span className="text-[#10B981]">deploy</span>
                  <span className="text-gray-400">()</span>
                  <span className="text-white">;</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* No scroll indicator */}
      </section>

      {/* Social Proof Bar */}
      <section data-testid="social-proof-section" className="py-10 border-y border-white/5 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Stats */}
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-10">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i}
                  className="text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold text-white" data-testid={`stat-value-${i}`}>{s.value}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">{s.label}</div>
                </motion.div>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden lg:block w-px h-12 bg-white/10" />

            <div className="flex flex-col items-center gap-4">
              {/* Client logos */}
              <div className="flex flex-wrap items-center justify-center gap-8 md:gap-10">
                <span className="text-[10px] text-neutral-400 uppercase tracking-widest hidden md:block">Trusted By</span>
                {[
                  { name: "Google", url: "https://www.maxiomtech.com/images/logos/logo-3-black.svg" },
                  { name: "BMW", url: "https://www.maxiomtech.com/images/logos/Mask-Group-6.png" },
                  { name: "Exelis", url: "https://www.maxiomtech.com/images/logos/exelis-3-LOGO.png" },
                  { name: "Telus", url: "https://www.maxiomtech.com/images/logos/Mask-Group-5.png" },
                ].map((logo) => (
                  <img
                    key={logo.name}
                    src={logo.url}
                    alt={`${logo.name} logo`}
                    className="h-6 md:h-7 w-auto brightness-0 invert object-contain opacity-50 hover:opacity-80 transition-opacity"
                    loading="lazy"
                    data-testid={`trusted-logo-${logo.name.toLowerCase()}`}
                  />
                ))}
              </div>

              {/* Clutch badge */}
              <a
                href="https://clutch.co/profile/maxiom-technology"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-gray-200 hover:border-[#00AEEF]/60 hover:bg-[#00AEEF]/10 transition-colors"
                data-testid="clutch-badge"
              >
                <span className="flex h-6 w-24 items-center justify-center rounded bg-transparent text-[13px] font-bold text-[#F59E0B] tracking-tight leading-none">
                  ★★★★★
                </span>
                <span className="font-semibold text-white">Rated on Clutch</span>
                <span className="text-gray-400 hidden sm:inline">
                  Maxiom Technology profile
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Free tools: idea assessment + cost estimator */}
      <section
        data-testid="home-free-tools"
        className="py-20 md:py-32 bg-[#0A0A0A] border-y border-white/5"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mb-12 md:mb-14">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-3">
              Free tools
            </p>
            <h2 className="font-bold text-3xl md:text-4xl lg:text-[2.5rem] leading-tight text-white tracking-tight">
              De-risk your product before you commit capital
            </h2>
            <p className="text-neutral-400 text-[15px] md:text-base leading-relaxed mt-4">
              Run the idea assessment for a viability readout, or ballpark an indicative build range
              in about three minutes, both reviewed by senior engineers.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
            {/* App Idea Assessment */}
            <div
              className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-8 md:p-10 flex flex-col h-full"
              data-testid="idea-validator-teaser"
            >
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#00AEEF] to-[#06B6D4]" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#00AEEF]/10 via-transparent to-transparent pointer-events-none" />
              <div className="relative flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 bg-[#00AEEF]/10 border border-[#00AEEF]/30 rounded-full px-4 py-[6px] text-[12px] font-medium text-[#06B6D4] tracking-[0.5px] mb-6 w-fit">
                  Free App Idea Assessment
                </div>

                <h3 className="font-bold text-[clamp(24px,4vw,36px)] leading-[1.15] mb-5 text-white">
                  How Good is Your{" "}
                  <span className="bg-gradient-to-r from-[#00AEEF] to-[#06B6D4] bg-clip-text text-transparent">
                    App Idea?
                  </span>
                </h3>

                <p className="text-neutral-400 text-[15px] leading-relaxed mb-8 flex-1">
                  Answer 8 questions. A senior engineer reads the result. You get a viability
                  read and a next step, not a drip sequence. We&apos;ve been shipping since 2002.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 flex-wrap mb-8">
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <Timer className="w-5 h-5 text-[#00AEEF] shrink-0" />
                    <span>Takes about 2 minutes</span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <BarChart3 className="w-5 h-5 text-[#00AEEF] shrink-0" />
                    <span>Free personalized report</span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <Lock className="w-5 h-5 text-[#00AEEF] shrink-0" />
                    <span>No spam, ever</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    onClick={() => setIdeaValidatorOpen(true)}
                    className="w-full sm:w-auto shrink-0 bg-gradient-to-r from-[#00AEEF] to-[#2563eb] hover:from-[#00AEEF] hover:to-[#1d4ed8] text-white font-bold px-10 py-5 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)]"
                  >
                    Start Your Free Assessment <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Link
                    to="/lp/free-app-idea-assessment"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#06B6D4] hover:gap-3 transition-all sm:justify-end"
                  >
                    Read more <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>
                </div>
              </div>
            </div>

            {/* App cost estimator */}
            <div
              className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-8 md:p-10 flex flex-col h-full"
              data-testid="home-cost-estimator-column"
            >
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#06B6D4] to-[#00AEEF]" />
              <div className="absolute inset-0 bg-gradient-to-bl from-[#06B6D4]/[0.07] via-transparent to-transparent pointer-events-none" />
              <div className="relative flex flex-col flex-1">
                <div className="inline-flex items-center gap-2 bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-full px-4 py-[6px] text-[12px] font-medium text-[#06B6D4] tracking-[0.5px] mb-6 w-fit">
                  Free App Cost Estimate
                </div>
                <h3 className="font-bold text-[clamp(24px,4vw,36px)] leading-[1.15] mb-5 text-white">
                  What Will Your{" "}
                  <span className="bg-gradient-to-r from-[#06B6D4] to-[#00AEEF] bg-clip-text text-transparent">
                    App Actually Cost?
                  </span>
                </h3>
                <p className="text-neutral-400 text-[15px] leading-relaxed mb-8 flex-1">
                  Six questions → indicative low / expected / high range, calibrated against real builds.
                  For a binding quote, we&apos;ll follow up after review.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 flex-wrap mb-8">
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <ListChecks className="w-5 h-5 text-[#06B6D4] shrink-0" />
                    <span>Six quick questions</span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <BarChart3 className="w-5 h-5 text-[#06B6D4] shrink-0" />
                    <span>Low / expected / high preview</span>
                  </div>
                  <div className="flex items-center gap-2 text-neutral-400 text-[13px]">
                    <Lock className="w-5 h-5 text-[#06B6D4] shrink-0" />
                    <span>No spam, ever</span>
                  </div>
                </div>

                {costEstimateLanding ? (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Button
                      type="button"
                      onClick={() => setCostEstimatorOpen(true)}
                      className="w-full sm:w-auto shrink-0 bg-gradient-to-r from-[#00AEEF] to-[#2563eb] hover:from-[#00AEEF] hover:to-[#1d4ed8] text-white font-bold px-10 py-5 rounded-[10px] shadow-[0_4px_20px_rgba(0,174,239,0.3)] hover:shadow-[0_8px_30px_rgba(0,174,239,0.4)]"
                    >
                      Start Your Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Link
                      to="/lp/free-app-cost-estimate"
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#06B6D4] hover:gap-3 transition-all sm:justify-end"
                    >
                      Read more <ArrowRight className="w-4 h-4 shrink-0" />
                    </Link>
                  </div>
                ) : (
                  <Link
                    to="/lp/free-app-cost-estimate"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-[10px] border border-white/15 bg-[#0A0A0A] px-6 py-4 text-sm font-semibold text-white hover:border-[#00AEEF]/50 transition-colors"
                  >
                    Open cost estimator <ArrowRight className="w-4 h-4 shrink-0" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">What We Do</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Areas of Expertise</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
              >
                <Link
                  to={s.path}
                  className="block bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#00AEEF]/50 transition-all duration-300 group h-full"
                  data-testid={`service-card-${i}`}
                >
                  <s.icon className="w-10 h-10 text-[#00AEEF] mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold text-white mb-3">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <span className="text-[#00AEEF] text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn More <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="process-section" className="py-20 md:py-32 bg-[#0f0f0f]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">How We Work</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">How we build.</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <motion.div
                key={p.step}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="relative"
                data-testid={`process-step-${i}`}
              >
                <div className="text-5xl sm:text-6xl font-bold tabular-nums tracking-tight text-[#00AEEF]/45 mb-4">
                  {p.step}
                </div>
                <h3 className="text-2xl font-semibold text-white mb-3">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                {i < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 right-0 translate-x-1/2 w-8">
                    <ArrowRight className="w-5 h-5 text-[#00AEEF]/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section data-testid="case-studies-preview" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Our Work</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight">Recent Projects</h2>
            </div>
            <Link to="/case-studies" className="text-[#00AEEF] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-case-studies">
              View All Case Studies <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {caseStudies.map((cs, i) => (
              <motion.div
                key={cs.name}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
              >
                <a
                  href={cs.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#00AEEF]/30 transition-all duration-300 group"
                  data-testid={`case-study-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="text-xs text-[#00AEEF] uppercase tracking-widest font-medium">{cs.category}</span>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${cs.color}15`, color: cs.color }}>
                      {cs.initials}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-white mt-1 mb-2 group-hover:text-[#00AEEF] transition-colors">{cs.name}</h3>
                  <p className="text-gray-400 text-sm">{cs.desc}</p>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-20 md:py-32 bg-[#0f0f0f]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
            <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">Testimonials</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight">What Our Clients Say</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                className="bg-[#121212] border border-white/5 rounded-xl p-8"
                data-testid={`testimonial-${i}`}
              >
                <div className="text-[#00AEEF] text-4xl mb-4">&ldquo;</div>
                <p className="text-gray-300 text-sm leading-relaxed mb-6">{t.quote}</p>
                <div className="flex items-center gap-3">
                  <img src={t.photo} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-[#00AEEF]/30" loading="lazy" />
                  <div>
                    <div className="text-white font-medium text-sm">{t.name}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{t.title}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content - App Development Services */}
      <section data-testid="seo-content-section" className="py-20 md:py-32 bg-[#0A0A0A] border-t border-white/5">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mb-4">About Maxiom Apps</p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">An app studio in the DC Metro</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Maxiom Apps builds custom mobile, web, SaaS, and AI software from Ashburn, VA. We have been shipping since 2002 ($100M+ contract value, 221+ clients). Named work includes AT&T, Microsoft, BMW, and Toastmasters International; those write-ups live on maxiomtech.com.
                </p>
                <p>
                  Senior engineers, UX, and product people on one team. Native iOS and Android, React Native or Flutter when that is the honest architecture, React and Next.js on the web, production LLM features when they earn their keep. If the stack is wrong, we say so before you fund a sprint.
                </p>
                <p>
                  Delivery is two-week slices you can see: demos, PRs, and a scope you can hold us to. We do not invent local offices. The studio is here. The code ships from here.
                </p>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { title: "iOS App Development", desc: "Native Swift and SwiftUI apps for iPhone, iPad, and Apple Watch with App Store optimization." },
                  { title: "Android App Development", desc: "Kotlin and Jetpack Compose apps for the full range of Android phones, tablets, and wearables." },
                  { title: "React & Angular Development", desc: "Modern SPA and SSR web applications with React, Next.js, Angular, and Vue.js frameworks." },
                  { title: "AI & ChatGPT Integration", desc: "LLM-powered chatbots, predictive analytics, computer vision, and generative AI applications." },
                  { title: "E-commerce Solutions", desc: "Shopify Plus, headless commerce, marketplace platforms, and mobile shopping apps." },
                  { title: "Cloud & DevOps", desc: "AWS, Azure, and GCP infrastructure with CI/CD pipelines, containers, and auto-scaling." },
                ].map((item, i) => (
                  <div key={i} className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#00AEEF]/20 transition-all" data-testid={`seo-card-${i}`}>
                    <h3 className="text-white font-medium text-sm mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <SpecializedSolutionsHub />

      {/* CTA with Lead Capture */}
      <section data-testid="cta-section" className="py-20 md:py-32 bg-[#0f0f0f]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                If this is the work, schedule a call.
              </h2>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                Send the constraint, the stack, and the deadline. A senior engineer replies within one business day. No deck theater.
              </p>
              <ul className="space-y-3">
                {["A senior engineer, not a BDR", "Indicative timeline and budget after we see the work", "Stack recommendation, including when not to build", "No obligation"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#00AEEF] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <LeadCaptureForm
              onFirstNameChange={(firstName) =>
                setIdeaValidatorFirstName(firstName)
              }
            />
          </div>
        </div>
      </section>

      <IdeaValidatorModal
        open={ideaValidatorOpen}
        onOpenChange={setIdeaValidatorOpen}
        initialFirstName={ideaValidatorFirstName}
        initialStep={1}
      />
      {costEstimateLanding ? (
        <AppCostEstimatorModal
          open={costEstimatorOpen}
          onOpenChange={setCostEstimatorOpen}
          page={costEstimateLanding}
        />
      ) : null}
    </>
  );
}
